import * as Sentry from '@sentry/react'

import { ApiErrorClassificationInterface } from '../classifyApiError'

/**
 * Captures a classified API error to Sentry with consistent tags and context.
 * Should be called only after silent kinds (abort / unauthorized / rate_limit)
 * have been filtered out upstream.
 *
 * Decision table:
 *  Error instance         → captureException directly (preserves stack trace)
 *  { response: Response } → extracts http metadata, creates synthetic Error
 *  Anything else          → serializes payload, creates synthetic Error
 */
export const captureApiError = (error: unknown, { kind, status }: ApiErrorClassificationInterface): void => {
  Sentry.withScope((scope) => {
    scope.setTag('api_error_kind', kind)

    if (status !== undefined) {
      scope.setTag('api_error_status', String(status))
    }

    if (error instanceof Error) {
      Sentry.captureException(error)

      return
    }

    if (error !== null && typeof error === 'object' && 'response' in error && (error as Record<string, unknown>).response instanceof Response) {
      const { response: httpResponse, ...rest } = error as { [key: string]: unknown; response: Response }

      scope.setExtras({ httpType: httpResponse.type, httpURL: httpResponse.url })

      try {
        scope.setExtra('error_payload', JSON.stringify(rest))
      } catch {
        scope.setExtra('error_payload', String(rest))
      }
    } else {
      try {
        scope.setExtra('error_payload', JSON.stringify(error))
      } catch {
        scope.setExtra('error_payload', String(error))
      }
    }

    const statusSuffix = status === undefined ? '' : ` (${status})`

    Sentry.captureException(new Error(`API ${kind} error${statusSuffix}`))
  })
}
